import { FC, Fragment, ReactNode } from 'react';

import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import { EmptySearchList } from '../../../../../components/empty-search-list/index';
import RootLoader from '../../../../../components/root-loader/index';
import { ScenarioHooks } from '../../../../../hooks/index';
import { ScenarioType } from '../../../../../types/scenario-modeling.types';
import { useScenarioCapTableStore } from '../../../cap-table.store';
import {
  CapTableTab,
  Column,
  columnTitle,
  shareClassesViewByColumns,
  StakeholdersViewBy,
  stakeholdersViewByColumns,
} from '../liquidity.types';
import { ShareClassesTab, StakeholdersTab } from '../tabs/index';

export type TableProps = {
  companyId: string;
  scenarioId: string;
  tab: CapTableTab;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
};

export type TabProps = {
  companyId: string;
  scenarioId: string;
  selectedViewBy: 'all' | StakeholdersViewBy;
  viewBy?: Record<StakeholdersViewBy, boolean>;
  visibleColumns: Column[];
};

export const capTableViewBy: Record<CapTableTab, (props: TabProps) => ReactNode> = {
  [CapTableTab.BY_SHARE_CLASS]: (props) => <ShareClassesTab {...props} />,
  [CapTableTab.BY_STAKEHOLDER]: (props) => <StakeholdersTab {...props} />,
};

export const Table: FC<TableProps> = ({ viewBy, visibleColumns, tab, companyId, scenarioId }) => {
  const {
    data: shareClassOrderData,
    isLoading,
    isSuccess,
  } = ScenarioHooks.useCapTableShareClasses<ScenarioType.LIQUIDITY>({ scenarioId });
  const { data: summarizeData } = ScenarioHooks.useCapTableSummarize<ScenarioType.LIQUIDITY>();

  const isEmptySearch = useScenarioCapTableStore(({ isEmptySearch }) => isEmptySearch);

  if (isLoading || !isSuccess) return <RootLoader />;

  const selectedViewBy: 'all' | StakeholdersViewBy =
    viewBy?.stakeholders && viewBy?.type
      ? 'all'
      : viewBy?.type
        ? StakeholdersViewBy.TYPE
        : StakeholdersViewBy.STAKEHOLDERS;

  const possibleShareClasses = shareClassOrderData?.filter(({ isDefault }) => !isDefault);
  const senioritiesFromSummarize = Object.values(summarizeData?.seniorityInfo || {});

  const seniorities = senioritiesFromSummarize.length
    ? senioritiesFromSummarize
    : shareClassOrderData?.[0]?.seniorityColumns || [];

  const additionalColumns: PartialKeys<Column, string[]> = {
    [Column.OTHER_SHARE_CLASSES]: possibleShareClasses?.map(({ name }) =>
      name.length > 30 ? name.substring(0, 30) + '...' : name,
    ),
    [Column.OTHER_SHARE_CLASSES_PROCEEDS]: possibleShareClasses?.map(({ name }) =>
      name.length > 30 ? name.substring(0, 30) + '...' : name,
    ),
    [Column.SENIORITY]: seniorities?.map(({ seniorityIndex }) => seniorityIndex.toString()),
  };

  const columns =
    tab === CapTableTab.BY_SHARE_CLASS
      ? shareClassesViewByColumns
      : stakeholdersViewByColumns[selectedViewBy];

  if (isEmptySearch) return <EmptySearchList className="m-auto my-20" />;

  return (
    <>
      <table className="w-full shrink-0 overflow-hidden text-nowrap">
        <thead className="h-11 border-y border-[#F2F2F2] bg-gray-50 px-4">
          <tr>
            <th className="pl-4 text-left">
              <span className="text-label-md font-medium text-[#172335]" />
            </th>
            {columns.map(
              (fieldNameKey) =>
                visibleColumns.includes(fieldNameKey) &&
                (additionalColumns?.[fieldNameKey] ? (
                  additionalColumns?.[fieldNameKey].map((fieldName) => {
                    if (fieldNameKey !== Column.OTHER_SHARE_CLASSES_PROCEEDS)
                      return (
                        <th className="px-4 pr-6 text-left" key={fieldName}>
                          <span className="text-label-md font-medium text-[#172335]">
                            {fieldNameKey === Column.SENIORITY && (
                              <AppFormattedMessage id={columnTitle[Column.SENIORITY]} />
                            )}{' '}
                            {fieldName}
                          </span>
                        </th>
                      );

                    return (
                      <Fragment key={`${fieldName}-${Column.INVESTMENT}`}>
                        {visibleColumns.includes(Column.INVESTMENT) && (
                          <th className="px-4 pr-6 text-left">
                            <span className="text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id={columnTitle[Column.INVESTMENT]} />
                              {' - '}
                              {fieldName}
                            </span>
                          </th>
                        )}
                        {visibleColumns.includes(Column.MULTIPLE) && (
                          <th className="px-4 pr-6 text-left">
                            <span className="text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id={columnTitle[Column.MULTIPLE]} /> {fieldName}
                            </span>
                          </th>
                        )}
                        {visibleColumns.includes(Column.INVESTMENT_TIME) && (
                          <th className="px-4 pr-6 text-left">
                            <span className="text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id={columnTitle[Column.INVESTMENT_TIME]} />
                            </span>
                          </th>
                        )}
                        {visibleColumns.includes(Column.CAP_PARTICIPATING) && (
                          <th className="px-4 pr-6 text-left">
                            <span className="text-label-md font-medium text-[#172335]">
                              <AppFormattedMessage id={columnTitle[Column.CAP_PARTICIPATING]} />{' '}
                              {fieldName}
                            </span>
                          </th>
                        )}
                      </Fragment>
                    );
                  })
                ) : (
                  <th className="px-4 pr-6 text-left" key={fieldNameKey}>
                    <span className="text-label-md font-medium text-[#172335]">
                      <AppFormattedMessage id={columnTitle[fieldNameKey]} />
                    </span>
                  </th>
                )),
            )}
          </tr>
        </thead>
        <tbody>
          {capTableViewBy[tab]({
            visibleColumns,
            companyId,
            selectedViewBy,
            viewBy,
            scenarioId,
          })}
        </tbody>
      </table>
    </>
  );
};
